import React, { useEffect, useState } from "react";
import { queryPlannerItemsAsync } from "src/api/plannerApi";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { Label, Loader, UnstyledButton } from "src/ui";
import moment from "moment";
import { useSelector } from "react-redux";
import { getLangCode } from "src/l10n/localize";
import CountdownChart from "./CountdownChart";
import { Stack } from "@fluentui/react";
import "./TimelineAgenda.scss";
import classNames from "classnames";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { Style } from "src/ui/helpers";

interface IProps {
    onItemClick?: (item: Spintr.PlannerItem) => void;
}

const TimelineAgenda = (props: IProps) => {
    const [ongoingItems, setOngoingItems] = useState<Spintr.PlannerItem[]>([]);
    const [upcomingItems, setUpcomingItems] = useState<Record<string, Spintr.PlannerItem[]>>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showAllOngoingItems, setShowAllOngoingItems] = useState<boolean>(false);
    const [showAllUpcomingItems, setShowAllUpcomingItems] = useState<boolean>(false);

    const langCode = useSelector<Spintr.AppState, string>((state) =>
        getLangCode(parseInt(state.profile.active.settings.language, 10) || 3)
    );

    useEffect(() => {
        const now = new Date();
        queryPlannerItemsAsync({
            after: now,
            status: SpintrTypes.ContentStatus.Published,
        }).then((data) => {
            const ongoing = data.items.filter((item) => item.startsAt <= now && item.endsAt >= now);
            const upcoming = data.items
                .filter((item) => item.startsAt > now)
                .reduce((groups, item) => {
                    const monthYear = moment(item.startsAt).format("MMMM YYYY");

                    if (!groups[monthYear]) {
                        groups[monthYear] = [];
                    }

                    groups[monthYear].push(item);

                    return groups;
                }, {} as Record<string, Spintr.PlannerItem[]>);

            setOngoingItems(ongoing);
            setUpcomingItems(upcoming);
            setIsLoading(false);
        });
    }, []);

    const renderUpcomingItems = () => {
        let displayedCount = 0;

        return Object.entries(upcomingItems).map(([monthYear, items]) => {
            const month = monthYear.split(" ")[0];
            const filteredItems = showAllUpcomingItems
                ? items
                : items.filter(() => displayedCount < 10 && ++displayedCount <= 10);

            if (filteredItems.length === 0) return null;

            return (
                <div key={monthYear}>
                    <Label size="h6" weight="medium" className="month">
                        {month}
                    </Label>
                    {filteredItems.map((item) => renderItem(item))}
                </div>
            );
        });
    };

    const renderItem = (item: Spintr.PlannerItem, isOngoing?: boolean) => {
        const ongoingWorkShift = item.itemType === SpintrTypes.PlannerItemType.WorkShift && isOngoing;
        return (
            <UnstyledButton
                className={classNames("item", {
                    ongoingWorkShift,
                })}
                onClick={() => props.onItemClick?.(item)}
                key={item.id}
                style={ongoingWorkShift ? undefined : { borderLeftColor: item.color }}
            >
                <Stack horizontal tokens={{ childrenGap: Style.getSpacing(6) }} verticalAlign="center">
                    {ongoingWorkShift && <CountdownChart item={item} />}
                    <div>
                        <Label size="h5" weight="medium">
                            {item.name}
                        </Label>
                        <Label as="span" size="h6" color="primaryContent">
                            {item.category.isLangTag ? localize(item.category.name) : item.category.name}
                        </Label>{" "}
                        <Label as="span" size="h6" color="primaryContent" weight="normal">
                            {moment(item.startsAt).isSame(item.endsAt, "day") ? (
                                <>
                                    {!isOngoing && (
                                        <>
                                            {item.startsAt.toLocaleString(langCode, {
                                                month: "long",
                                                day: "numeric",
                                            })}
                                            ,{" "}
                                        </>
                                    )}
                                    {moment(item.startsAt).format("LT")} - {moment(item.endsAt).format("LT")}
                                </>
                            ) : (
                                <>
                                    {localize("Startar")}:{" "}
                                    {item.startsAt.toLocaleString(langCode, {
                                        month: "long",
                                        day: "numeric",
                                    })}
                                    , {moment(item.startsAt).format("LT")}, {localize("Slutar")}:{" "}
                                    {item.endsAt.toLocaleString(langCode, {
                                        month: "long",
                                        day: "numeric",
                                    })}
                                    , {moment(item.endsAt).format("LT")}
                                </>
                            )}
                        </Label>
                    </div>
                </Stack>
            </UnstyledButton>
        );
    };

    const renderViewAllButton = (isOngoing?: boolean) => (
        <UnstyledButton
            className="view-all"
            onClick={() => (isOngoing ? setShowAllOngoingItems(true) : setShowAllUpcomingItems(true))}
        >
            <Stack horizontal tokens={{ childrenGap: Style.getSpacing(2) }} verticalAlign="center">
                <Label as="span" color="light-blue">
                    {localize("VisaAlla")} (
                    {isOngoing ? ongoingItems.length : Object.values(upcomingItems).flat().length})
                </Label>
                <Visage2Icon icon="arrow-down-1" color="light-blue" size="small" />
            </Stack>
        </UnstyledButton>
    );

    return (
        <div className="TimelineAgenda">
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    {ongoingItems.length === 0 && Object.keys(upcomingItems).length === 0 && (
                        <Label>{localize("IngaTraffar")}</Label>
                    )}
                    {ongoingItems.length > 0 && (
                        <div>
                            <Label weight="medium" size="body-5">
                                {localize("Pagaende")}
                            </Label>
                            {ongoingItems
                                .filter((_, index) => showAllOngoingItems || index < 10)
                                .map((item) => renderItem(item, true))}
                            {ongoingItems.length > 10 && !showAllOngoingItems && renderViewAllButton(true)}
                        </div>
                    )}

                    {Object.keys(upcomingItems).length > 0 && (
                        <div>
                            <Label weight="medium" size="body-5">
                                {localize("Kommande")}
                            </Label>
                            {renderUpcomingItems()}
                            {Object.values(upcomingItems).flat().length > 10 &&
                                !showAllUpcomingItems &&
                                renderViewAllButton()}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default TimelineAgenda;
